import {
  Boxes,
  ClipboardList,
  Clock,
  Handshake,
  PackageCheck,
  ShoppingCart,
  Store,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type NavChild = {
  label: string;
  to: string;
  /** The permission the child page's API actually requires. */
  perm: string;
  /** Exact-path match (for routes that are a prefix of a sibling). */
  end?: boolean;
  /** A tenant switch the page also needs (it is hidden while that is off). */
  flag?: NavFlag;
};

/** Tenant switches a nav entry can depend on, from the public tenant config. */
export type NavFlag = 'posTopupEnabled';

export type NavGroup = {
  label: string;
  icon: LucideIcon;
  /** Where the group header navigates. Re-pointed to the first VISIBLE child. */
  to: string;
  /** Only for a flat (childless) group. */
  perm?: string;
  end?: boolean;
  children?: NavChild[];
  /**
   * Routes that belong to this group without being a destination in it: a step
   * of a flow that starts here. Each is a path prefix. They keep the group lit
   * so the cashier still sees where they are, and are never offered as links.
   */
  steps?: string[];
};

/**
 * Cashier navigation, grouped for the sidebar.
 *
 * Each entry is paired with the permission its landing page's API actually
 * requires. Rendering a link the holder can't use is worse than hiding it —
 * they land on the page and every fetch 403s with no explanation. Perms are
 * JWT-baked, so a grant reaches the cashier on their next sign-in.
 */
export const NAV: NavGroup[] = [
  {
    label: 'Sell',
    icon: ShoppingCart,
    to: '/',
    // A sale runs till → checkout → receipt.
    steps: ['/checkout', '/receipt'],
    children: [
      { label: 'Checkout', to: '/', perm: 'pos.checkout', end: true },
      { label: 'Sell to Stockist', to: '/stockist', perm: 'pos.checkout', end: true },
      // Cashier phase 5. Its member search rides pos.checkout too, which the
      // access tree nests it under.
      {
        label: 'Wallet Top-up',
        to: '/topup',
        perm: 'pos.topup',
        end: true,
        flag: 'posTopupEnabled',
      },
    ],
  },
  {
    label: 'Releasing',
    icon: PackageCheck,
    to: '/releasing',
    children: [
      { label: 'Releasing', to: '/releasing', perm: 'pos.release', end: true },
      { label: 'History', to: '/releasing/history', perm: 'pos.release' },
    ],
  },
  {
    label: 'Orders',
    icon: ClipboardList,
    to: '/transactions',
    children: [
      { label: 'Sales Orders', to: '/transactions', perm: 'pos.checkout' },
      { label: 'Orders', to: '/orders', perm: 'pos.orders.view' },
    ],
  },
  {
    label: 'Inventory',
    icon: Boxes,
    to: '/inventory',
    // Its own read-only perm (backfilled to every role that held pos.checkout
    // when it was introduced, so nobody lost the page).
    perm: 'pos.inventory.view',
  },
  // Accounting v1 — two-step consignment. The whole group additionally hides
  // unless the tenant's `consignment_enabled` flag is on (the sidebar filters
  // it out via public /tenant/config; the server re-gates every route).
  {
    label: 'Consignment',
    icon: Handshake,
    to: '/consignment',
    children: [
      { label: 'Consignees', to: '/consignment', perm: 'consignment.read', end: true },
      { label: 'Consign Out', to: '/consignment/new', perm: 'consignment.consign_out', end: true },
    ],
  },
  // Track 2A — stockist consignment. Same shape as the group above, but
  // BRANCH-keyed: the debtor is a stockist branch, not a person. Hidden unless
  // `stockist_consignment_enabled` is on (default OFF everywhere).
  {
    label: 'Stockist Consignment',
    icon: Store,
    to: '/stockist-consignment',
    children: [
      {
        label: 'Stockists',
        to: '/stockist-consignment',
        perm: 'stockist_consignment.read',
        end: true,
      },
      {
        label: 'Consign Out',
        to: '/stockist-consignment/new',
        perm: 'stockist_consignment.consign_out',
        end: true,
      },
    ],
  },
  {
    label: 'Shift',
    icon: Clock,
    to: '/time-logs',
    children: [
      { label: 'Time Logs', to: '/time-logs', perm: 'pos.checkout' },
      { label: 'Daily Report', to: '/daily-report', perm: 'pos.checkout' },
      { label: 'End Shift', to: '/end-shift', perm: 'pos.checkout' },
    ],
  },
];

/** Drop the children whose tenant switch is off (a group left empty goes too). */
export function withTenantFlags(
  items: NavGroup[],
  flags: Partial<Record<NavFlag, boolean>>,
): NavGroup[] {
  return items.flatMap((group) => {
    if (!group.children) return [group];
    const children = group.children.filter((c) => !c.flag || flags[c.flag] === true);
    return children.length ? [{ ...group, children }] : [];
  });
}

export type NavViewer = { isSuper: boolean; perms: string[] };

/**
 * Drop everything the viewer can't reach: invisible children, then any group
 * left with no children. A surviving group is re-pointed at its first VISIBLE
 * child, so clicking a group header never lands on a forbidden page.
 */
export function visibleNav(items: NavGroup[], viewer: NavViewer): NavGroup[] {
  const allowed = (perm: string) => viewer.isSuper || viewer.perms.includes(perm);
  return items.flatMap((group) => {
    if (!group.children) {
      return group.perm && !allowed(group.perm) ? [] : [group];
    }
    const children = group.children.filter((c) => allowed(c.perm));
    if (children.length === 0) return [];
    return [{ ...group, to: children[0]!.to, children }];
  });
}

export function isChildActive(child: { to: string; end?: boolean }, pathname: string): boolean {
  if (child.end) return pathname === child.to;
  return pathname === child.to || pathname.startsWith(`${child.to}/`);
}

export function isGroupActive(group: NavGroup, pathname: string): boolean {
  if (group.steps?.some((s) => pathname === s || pathname.startsWith(`${s}/`))) return true;
  if (group.children?.length) return group.children.some((c) => isChildActive(c, pathname));
  return isChildActive(group, pathname);
}

/** What works with no internet: selling, and closing the shift. */
const OFFLINE_ROUTES = new Set(['/', '/end-shift']);

/** The nav an offline till shows. Anything else needs the server. */
export function offlineNav(groups: NavGroup[]): NavGroup[] {
  return groups.flatMap((g) => {
    if (g.children) {
      const children = g.children.filter((c) => OFFLINE_ROUTES.has(c.to));
      return children.length > 0 ? [{ ...g, children }] : [];
    }
    return OFFLINE_ROUTES.has(g.to) ? [g] : [];
  });
}
